import { useRef, useState } from "react"
import { FoundationsButton } from "../../GenericComponents/button"
import { NTSP_StatPortalProps } from "./Ntsp_Slice"
import { PatientDetailsHeader } from "./PatientHeader"
import { validateForm } from "./NTSP_StatPortal"
import { usePromiseDebounce } from "../../Hooks/usePromiseDebounce" //FP-12575 Debounce save for UI actions
declare let window: any
const UnicornApp = window.unicornInstance as any
//FP-14209 - NTSP Stat Portal - Process Issues Tab Component
const ProcessIssues: React.FC<NTSP_StatPortalProps> = ({ form, setForm, handleTabSwitch, handleSave, handleClear, NTSPElements, validationSchema,
   }) => {
    if (validationSchema === undefined) validationSchema = {}
    const [errors, setErrors] = useState<{ [s: string]: string }>({}) //FP-14209 - state to hold validation errors for the form
    const [submitted, setSubmitted] = useState<boolean>(false)
    const [formError, setFormError] = useState<string>('')
    const [saving, setSaving] = useState<boolean>(false)
    const formRef = useRef(null as any)
    const [isCustomerSite] = useState<boolean>(UnicornApp.checkUserPrivileges("NTSPCustomer"))
    const debouncedHandleSave = usePromiseDebounce(async (...args: Parameters<typeof handleSave>) => {
        return await Promise.resolve(handleSave(...args));
    }); //FP-12575 debounce to prevent multiple rapid clicks    
    const handleChange = (e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value, type } = e.target
        let updatedValue: string | number = value

        setForm((prev) => ({
            ...prev,
            [name]: updatedValue
        }))

        // Validate on change
        const schema = validationSchema[name]
        if (schema) {
            const tempErrors = { ...errors }
            tempErrors[name] = typeof schema === 'function' ? schema({ ...form, [name]: updatedValue }) : ''
            setErrors(tempErrors)
        }
    }

    const handleBlur = (e: React.FocusEvent<HTMLSelectElement | HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value, type } = e.target
        let updatedValue: string | number = value

        if (type === 'select-one' && name.startsWith('fk_')) {
            updatedValue = Number(value)
        }

        if (validationSchema[name]) {
            setErrors((prev: { [s: string]: string }) => ({
                ...prev,
                [name]: validationSchema[name]({ ...form, [name]: updatedValue }),
            }))
        }
    }

    const wrapHandleClear = async () => {
        setSubmitted(true)
        const tempErrors = validateForm(form, validationSchema)
        const allErrors = { ...errors, ...tempErrors }
        setErrors(allErrors)
        if (Object.values(allErrors).some(Boolean)) {
            setFormError('Please fix the errors before saving.')
            return
        }
        setFormError('')
        if (handleSave) await handleSave(true)
        if (handleClear) handleClear()
    }

  const wrapHandleSave = async () => { //FP-14209 - wrapper function for handleSave to perform validation before saving the form
    setSubmitted(true)
    setSaving(true)
    const tempErrors = validateForm(form, validationSchema)
    const allErrors = { ...errors, ...tempErrors }
    setErrors(allErrors)
    if (Object.values(allErrors).some(Boolean)) {
        setFormError('Please fix the errors before saving.')
        setSaving(false)
        return
    } else {
        setErrors({})
        setFormError('')
        try {
            if (form?.CaseId) {
                if (debouncedHandleSave) await debouncedHandleSave(); //FP-12575 using debounced handle save to prevent multiple rapid clicks on save button, but await it to ensure completion before proceeding
            } else {
                setFormError('Case not found')
            }
        } catch (error) {
            setFormError('An unexpected error occurred while saving.')
        }
        setSaving(false)
        return
    }
}


    return (
        <>
            <div className='sticky-Patient-header'>
                <PatientDetailsHeader
                    form={form}
                    NTSPElements={NTSPElements}
                />
            </div>
            <div
                style={{
                    padding: '24px',
                }}
            >
                <form
                    onSubmit={(e) => {
                        e.preventDefault()
                        wrapHandleSave()
                    }}
                    className="ntsp-form-container"
                    style={{
                        padding: 0,
                        minHeight: '200px',
                    }}
                    ref={formRef}
                >
                    {/* Row 1 - All dropdowns and input */}
                    <div className="process-issue-container" >
                        {/* Column 1 - Impact of the Issue */}
                        <div className="process-issue-block">
                            <div className="form-group" style={{ marginBottom: 0 }}>
                                <label htmlFor="fk_ImpactOfIssue" style={{ marginBottom: '4px', display: 'block' }}>
                                    Impact of the Issue*:
                                </label>
                                <span style={{ width: '100%' }}>
                                    <select
                                        name="fk_ImpactOfIssue"
                                        id="fk_ImpactOfIssue"
                                        value={form.fk_ImpactOfIssue ?? -1}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        className="ntsp-fields"
                                        tabIndex={1}
                                    >
                                        <option value="-1">-Select Impact-</option>
                                        {NTSPElements?.Impacts?.slice().sort((a, b) => a._key - b._key).map((item) => (
                                            <option key={item._key} value={item._key}>
                                                {item.FriendlyName}
                                            </option>
                                        ))}
                                    </select>
                                    {errors.fk_ImpactOfIssue && (
                                        <div style={{ color: 'red', position: 'absolute', lineHeight: '1', fontSize: '12px' }}>
                                            {errors.fk_ImpactOfIssue}
                                        </div>
                                    )}
                                </span>
                            </div>
                        </div>
                        {/* Column 2 - Type of Issue */}
                        <div className="process-issue-block">
                            <div className="form-group" style={{ marginBottom: 0 }}>
                                <label htmlFor="fk_TypeOfIssue" style={{ marginBottom: '4px', display: 'block' }}>
                                    Type of Issue*:
                                </label>
                                <span style={{ width: '100%' }}>
                                    <select
                                        name="fk_TypeOfIssue"
                                        id="fk_TypeOfIssue"
                                        value={form.fk_TypeOfIssue ?? -1}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        className="ntsp-fields"
                                        tabIndex={2}
                                    >
                                        <option value="-1">-Select-</option>
                                        {NTSPElements?.TypeOfIssue?.slice().sort((a, b) => a._key - b._key).map((item) => (
                                            <option key={item._key} value={item._key}>
                                                {item.FriendlyName}
                                            </option>
                                        ))}
                                    </select>
                                    {errors.fk_TypeOfIssue && (
                                        <div style={{ color: 'red', position: 'absolute', lineHeight: '1', fontSize: '12px' }}>
                                            {errors.fk_TypeOfIssue}
                                        </div>
                                    )}
                                </span>
                            </div>
                        </div>
                        {/* Column 3 - Issue Status */}
                        <div className="process-issue-block">
                            <div className="form-group" style={{ marginBottom: 0 }}>
                                <label htmlFor="fk_IssueStatus" style={{ marginBottom: '4px', display: 'block' }}>
                                    Issue Status*:
                                </label>
                                <span style={{ width: '100%' }}>
                                    <select
                                        name="fk_IssueStatus"
                                        id="fk_IssueStatus"
                                        value={form.fk_IssueStatus ?? -1}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        className="ntsp-fields"
                                        tabIndex={3}
                                    >
                                        <option value="-1">-Select Status-</option>
                                        {NTSPElements?.IssueStatus?.slice().sort((a, b) => a._key - b._key).map((item) => (
                                            <option key={item._key} value={item._key}>
                                                {item.FriendlyName}
                                            </option>
                                        ))}
                                    </select>
                                    {errors.fk_IssueStatus && (
                                        <div style={{ color: 'red', position: 'absolute', lineHeight: '1', fontSize: '12px' }}>
                                            {errors.fk_IssueStatus}
                                        </div>
                                    )}
                                </span>
                            </div>
                        </div>
                        {/* Column 4 - Ticket # */}
                        <div className="process-issue-block">
                            <div className="form-group" style={{ marginBottom: 0 }}>
                                <label htmlFor="TicketNumber" style={{ marginBottom: '4px', display: 'block' }}>
                                    Ticket #*:
                                </label>
                                <span style={{ width: '100%' }}   >
                                    <input
                                        type="text"
                                        name="TicketNumber"
                                        id="TicketNumber"
                                        value={form.TicketNumber ?? ''}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        className="ntsp-fields"
                                        tabIndex={4}
                                        style={{ width: '100%' }}
                                        maxLength={20} //FP-14209 - assuming max length of ticket number is 20 characters
                                    />
                                    {errors.TicketNumber && (
                                        <div style={{ color: 'red', position: 'absolute', lineHeight: '1', fontSize: '12px' }}>
                                            {errors.TicketNumber}
                                        </div>
                                    )}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Row 2 - Describe the issue and Submit button inline */}
                    <div style={{
                        marginTop: '1.5rem',
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'flex-end',
                        gap: '16px',
                        width: '90%',
                    }}>
                        {/* Describe the issue - takes most width */}
                        <div className="form-group" style={{ flex: '1 1 1%', marginBottom: 0 }}>
                            <label htmlFor="IssueDescription" style={{ marginBottom: '4px', display: 'block' }}>
                                Describe the Issue*:
                            </label>
                            <span style={{ width: '100%' }}>
                                <textarea
                                    name="IssueDescription"
                                    id="IssueDescription"
                                    value={form.IssueDescription ?? ''}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    className="ntsp-fields"
                                    tabIndex={5}
                                    rows={4}
                                    style={{ width: '100%' }}
                                    maxLength={2000}
                                />
                                {errors.IssueDescription && (
                                    <div style={{ color: 'red', position: 'absolute', lineHeight: '1', fontSize: '12px' }}>
                                        {errors.IssueDescription}
                                    </div>
                                )}
                            </span>
                        </div>
                    </div>

                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            flexDirection: 'column',
                            marginTop: '2rem',
                        }}
                    >
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                            <FoundationsButton
                                type="submit"
                                buttonClasses="app-button"
                                tabIndex={40}
                                disabled={saving || (isCustomerSite && form.fk_Outcome !== -1)}
                                title={(isCustomerSite && form.fk_Outcome !== -1) ? "This form is no longer pending and cannot be edited at this site" : "Save the current form"}
                                id={''}
                            >
                                Save
                            </FoundationsButton>
                            <FoundationsButton
                                buttonClasses="app-button"
                                onClick={wrapHandleClear}
                                id={''}
                                disabled={saving || (isCustomerSite && form.fk_Outcome !== -1)}
                                toolTipText="Currently opened case entry form will be saved and closed."
                                tooltipClasses="info-icon"
                            >
                                Close
                            </FoundationsButton>
                        </div>
                        {submitted && formError !== '' && (
                            <div style={{ color: 'red', textAlign: 'right', marginTop: '0.5rem' }}>
                                {formError}
                            </div>
                        )}
                    </div>
                </form>
            </div >
        </>
    )
}

export default ProcessIssues